import React, { useState, useEffect } from 'react';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const SearchBar = ({ searchTerm, onSearchChange, totalSongs }) => {
  const [inputValue, setInputValue] = useState(searchTerm);
  const [isSearching, setIsSearching] = useState(false);

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (inputValue?.trim()) {
      setIsSearching(true);
      onSearchChange(inputValue?.trim());
      // Reset searching state after a short delay to show feedback
      setTimeout(() => setIsSearching(false), 1000);
    }
  };

  const handleInputChange = (e) => {
    const value = e?.target?.value;
    setInputValue(value);
    
    // Clear search when input is empty
    if (!value?.trim()) {
      onSearchChange('');
    }
  };

  const handleKeyDown = (e) => {
    if (e?.key === 'Enter') {
      e?.preventDefault();
      handleSubmit(e);
    }
  };

  const handleClear = () => {
    setInputValue('');
    onSearchChange('');
  };

  // Update input value when searchTerm prop changes (for external clear operations)
  React.useEffect(() => {
    setInputValue(searchTerm);
  }, [searchTerm]);

  return (
    <div className="bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-xl p-6 mb-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-white">Search Songs with Fuzzy Matching</h2>
        <span className="text-sm text-blue-100">{totalSongs} songs available</span>
      </div>
      
      <form onSubmit={handleSubmit} className="flex gap-2">
        <div className="relative flex-1">
          <Icon name="Search" size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-blue-200" />
          <Input
            type="text"
            placeholder="Search by musical, song title, composer, lyricist... (Press Enter to search)"
            value={inputValue}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            className="pl-10 pr-10 bg-white bg-opacity-10 border-white border-opacity-30 text-white placeholder-blue-200 rounded-lg"
          />
          {inputValue?.trim() && (
            <button
              type="button"
              onClick={handleClear}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-blue-200 hover:text-white transition-colors"
              aria-label="Clear search"
            >
              <Icon name="X" size={16} />
            </button>
          )}
        </div>
        
        <Button
          type="submit"
          variant="outline"
          iconName={isSearching ? "Loader2" : "Search"}
          iconPosition="left"
          className={`border-white text-white hover:bg-white hover:text-indigo-900 rounded-lg flex-shrink-0 ${
            isSearching ? "animate-spin" : ""
          }`}
          disabled={!inputValue?.trim() || isSearching}
        >
          {isSearching ? "Searching..." : "Search"}
        </Button>
      </form>
      
      {searchTerm && (
        <div className="mt-3 flex items-center justify-between text-sm text-blue-100">
          <span>
            Searching with fuzzy logic for: <span className="font-medium text-white">"{searchTerm}"</span>
          </span>
          <button
            onClick={handleClear}
            className="text-blue-200 hover:text-white transition-colors underline"
          >
            Clear search
          </button>
        </div>
      )}
      
      <div className="mt-2 text-xs text-blue-200">
        💡 Tip: Fuzzy search finds results even with typos or partial matches. Just press Enter to search!
      </div>
    </div>
  );
};

export default SearchBar;